import useFluxStore from "@armus/use-flux-store";
import { useMembersForExporter } from "data/selectors/aggregateMembers";
import userStore from "data/userStore";
import propertyComparatorBuilder from "data/util/propertyComparatorBuilder";
import Sidebar from "lib/hex/views/components/Sidebar";
import formRegistry from "lib/hex/views/formRegistry";
import PropTypes from "prop-types";
import React from "react";
import SplitButton from "../../common/components/SplitButton";
import exportParamsStore from "../../../data/exportParamsStore";
import Dispatcher from "../../../Dispatcher";
import ExportParamActions from "../../../data/ExportParamActions";
import serializeParams from "./components/serializeParams";

function ThereIsNoForm() {
    return <div className="form-section-single form-horizontal">
        <h2>
            There is no form.
        </h2>
        <p>
            This indicates a code and/or configuration - not licensing or
            permission - issue of some sort or another, which could be client-
            or server-side. Or both!
        </p>
    </div>;
}

function ExportForms({
                         rerun,
                     }) {
    const [formKey, setFormKey] = React.useState((rerun && rerun.key) || "");
    const [initialState, setInitialState] = React.useState(rerun && rerun.params);
    const [ exportState, setExportState ] = React.useState(undefined);
    const [ allMembers, canFilterByMember ] = useMembersForExporter(formKey);
    const [ activeMembers, setActiveMembers ] = React.useState(() =>
        rerun && rerun.params && rerun.params.activeMembers
            ? rerun.params.activeMembers.split(",")
            : [ "" ]);
    const forms = useFluxStore(() => {
        const forms = userStore.getHexForms()
            .sort(propertyComparatorBuilder("name", "key"));
        if (process.env.NODE_ENV === "development") {
            const key = "example";
            if (formRegistry.hasOwnProperty(key) && forms.every(f => f.key !== key)) {
                forms.push({
                    key,
                    name: "[[ Dev Example ]]",
                });
            }
        }
        // // Comment this in to allow accessing any form. In the case of aggregates, EXPORT support is assumed.
        // for (const key of Object.keys(formRegistry).sort()) {
        //     if (forms.find(it => it.key === key) != null) continue;
        //     forms.push({
        //         key,
        //         name: key
        //             .split("-")
        //             .map(s => s.length <= 3
        //                 ? s.toUpperCase()
        //                 : s.substr(0, 1).toUpperCase() + s.substr(1))
        //             .join(" "),
        //     });
        // }
        // // End access to all forms
        return forms;
    }, [ userStore ]);

    const allParamSetsLO = useFluxStore(
        () => exportParamsStore.getLoadObject(),
        [ exportParamsStore ],
    );

    const [ Form, impl, report ] = React.useMemo(() => {
        let report = "export";
        const Form = formRegistry[formKey];
        if (!formKey) {
            return [ null, null, report ];
        } else if (!Form) {
            return [ ThereIsNoForm, null, report ];
        }
        const parts = formKey.split("-");
        const last = parts[parts.length - 1];
        if (last === "export" || last === "harvest") {
            report = last;
        }
        return [ Form, Form ? Form.apiPrefix : null, report ];
    }, [ formKey ]);

    const allParamSets = React.useMemo(() => {
        if (!formKey || !Form.canRerun) return undefined;
        if (!allParamSetsLO.hasValue()) return undefined;
        const byFormKey = allParamSetsLO.getValueEnforcing()
            .reduce((agg, it) => ({
                ...agg,
                [it.typeKey]: (agg[it.typeKey] || []).concat(it),
            }), {});
        const items = byFormKey[formKey];
        if (!items || items.length === 0) return undefined;
        return items
            .map(it => ({
                ...it,
                key: it.id.toString(),
                canRerun: Form.canRerun(it.params),
            }))
            .sort(propertyComparatorBuilder("name", "id"));
    }, [ allParamSetsLO, formKey, Form ]);

    const [ activeParamSet, setActiveParamSet ] = React.useState();
    React.useEffect(() => {
        if (!activeParamSet) return;
        if (!allParamSets || allParamSets.every(it => it.id !== activeParamSet.id)) {
            setActiveParamSet(undefined);
        }
    }, [ allParamSets, activeParamSet ]);

    function addMembersToParams(params) {
        if (!params) params = {};
        if (!canFilterByMember) return params;
        if (!activeMembers || activeMembers.length === 0) return params;
        if (typeof params === "string") return params;
        if (params instanceof Array) return params;
        if (params.hasOwnProperty("activeMembers")) return params;
        if (activeMembers.every(it => it !== "")) {
            params = {
                ...params,
                "activeMembers": [ ...new Set(activeMembers) ].sort().join(","),
            };
        }
        return params;
    }

    const stateWithMembers = React.useMemo(
        () => addMembersToParams(exportState),
        [ exportState, activeMembers ],
    );

    const isParamSetRerunable = React.useMemo(
        () => {
            if (!formKey || !Form.canRerun) return false;
            if (!stateWithMembers) return false;
            return Form.canRerun(stateWithMembers);
        },
        [ Form, stateWithMembers ],
    );

    /**
     * Call me to get data from an endpoint of the exporter's API, and
     * asynchronously passed to an onData callback. For example:
     *
     * ```
     * this.needServerData({
     *     endpoint: "/date-range", // relative to the exporter's API
     *     params: {
     *         particid: e.target.value,
     *     },
     *     onData: this.buildDateRange,
     * });
     * ```
     *
     * @param options An object with at least `endpoint` and `onData` keys. A
     *  `params` key may be provided with any params to send to the endpoint.
     */
    function needServerData(options) {
        Backbone.dispatcher.trigger("load:data", impl, report, {
            ...options,
            params: serializeParams(addMembersToParams(options.params)),
        });
    }

    // because of marionette races, this reset must be imperative/synchronous...
    function handleFormSelect(key) {
        setFormKey(key);
        setInitialState(undefined);
        setExportState(undefined);
        setActiveMembers([ "" ]);
        setActiveParamSet(undefined);
    }

    const handleReady = React.useCallback(state =>
        setExportState(state), [setExportState]);

    const handleNotReady = React.useCallback(() =>
        setExportState(undefined), [setExportState]);

    function handleExport() {
        if (formKey && exportState) {
            Backbone.dispatcher.trigger(
                "get:export",
                impl,
                report,
                serializeParams(stateWithMembers),
            );
            handleFormSelect("");
        } else {
            console.error(`The '${formKey}' view is enabled, but has no state?!`);
        }
    }

    function handleParamSetSelect(key) {
        const p = (allParamSets || []).find(it => it.key === key);
        setActiveParamSet(p);
        setInitialState(p && p.params);
        setActiveMembers(p && p.params && p.params.activeMembers
            ? p.params.activeMembers.split(",")
            : [ "" ]);
    }

    function handleParamSetRename() {
        if (!activeParamSet) return;
        Dispatcher.dispatch({
            type: ExportParamActions.RENAME,
            id: activeParamSet.id,
            oldName: activeParamSet.name,
        });
    }

    function handleParamSetDelete() {
        Dispatcher.dispatch({
            type: ExportParamActions.DELETE,
            id: activeParamSet.id,
        });
    }

    function handleExportAndSaveNew() {
        Dispatcher.dispatch({
            type: ExportParamActions.CREATE,
            formKey,
            params: stateWithMembers,
        });
        handleExport();
    }

    function handleExportAndUpdateSelected() {
        Dispatcher.dispatch({
            type: ExportParamActions.UPDATE,
            id: activeParamSet.id,
            params: stateWithMembers,
        });
        handleExport();
    }

    let exportButton;
    if (isParamSetRerunable || (allParamSets && allParamSets.length)) {
        exportButton = <SplitButton
            key="export-button"
            type="primary"
            className="search-button btn-do-export"
            disabled={!exportState}
            actions={[
                {
                    icon: "search",
                    label: "Export",
                    value: "export",
                    onSelect: handleExport,
                }, {
                    label: "Export and Save New Parameter Selection",
                    value: "save",
                    disabled: !isParamSetRerunable,
                    onSelect: handleExportAndSaveNew,
                }, {
                    label: "Export and Update Parameter Selection",
                    value: "restore",
                    disabled: !isParamSetRerunable || !activeParamSet,
                    onSelect: handleExportAndUpdateSelected,
                },
            ]}
        />;
    } else {
        exportButton = <button
            className="btn btn-search btn-do-export"
            disabled={!exportState}
            type="submit"
            onClick={handleExport}
        >
            <span /> Export
        </button>;
    }

    return <div className="search-form-layout">
        <div className="row">
            <div className="col-md-12">
                <h1>Harvest and Export</h1>
            </div>
        </div>

        <div className="form-row form-container">
            <div className="form-section-container">
                <Sidebar
                    forms={forms}
                    formKey={formKey}
                    onFormSelect={handleFormSelect}
                    allParamSets={allParamSets}
                    activeParamSet={activeParamSet}
                    onParamSetSelect={handleParamSetSelect}
                    onParamSetRename={handleParamSetRename}
                    onParamSetDelete={handleParamSetDelete}
                    allMembers={canFilterByMember ? allMembers : null}
                    activeMembers={activeMembers}
                    onActiveMembersSelect={setActiveMembers}
                />
                {Form && <Form
                    initialState={initialState}
                    needServerData={needServerData}
                    onReady={handleReady}
                    onNotReady={handleNotReady}
                    aggregate={!!allMembers}
                    allMembers={allMembers}
                    activeMembers={canFilterByMember ? activeMembers : null}
                />}
            </div>
        </div>

        <div id="v-actions" className="form-row">
            <div className="form-actions">
                {exportButton}
            </div>
        </div>
    </div>;
}

ExportForms.propTypes = {
    rerun: PropTypes.shape({
        key: PropTypes.string.isRequired,
        params: PropTypes.shape({
            activeMembers: PropTypes.string,
            // ... et al
        }),
    }),
};

export default ExportForms;



// WEBPACK FOOTER //
// ./app/lib/hex/views/ExportForms.js